// Device (EventKit) calendars via @ebarooni/capacitor-calendar. Covers every
// account already on the iPhone — iCloud, Google, Outlook, Exchange — with
// no OAuth. Read-only. Dynamic import + web fallback.
//
// ⚠️ THENABLE-PROXY TRAP: a Capacitor plugin handle is a JS Proxy that
// intercepts EVERY property get — including `then` — so the proxy is a broken
// thenable. `await`ing it (or returning it from an async function, which
// awaits it internally) calls proxy.then(resolve, reject); those callbacks
// are swallowed by a bogus native "then" call and NEVER invoked, hanging the
// caller forever with no error. This was the root cause of the dead
// "Enable device calendar" button and the Health "Connecting…" hang.
// Rule: async helpers must wrap the proxy in a plain object ({ api }) and
// callers unwrap it. Never `return CapacitorCalendar` from an async function.

import { isNative, isIOS } from '../utils/platform';

// Race a native call against a timeout so an unauthorised/stalled EventKit
// bridge can never hang callers (the briefing, chat and the Calendar page all
// await this). Resolves to `fallback` if the plugin doesn't answer in time.
function withTimeout(promise, ms, fallback) {
  return Promise.race([
    Promise.resolve(promise).catch(() => fallback),
    new Promise((resolve) => setTimeout(() => resolve(fallback), ms)),
  ]);
}

// Resolves { api } (never the bare proxy — see thenable-proxy trap above) or
// null on web / import failure. The import is time-boxed as belt-and-braces.
async function plugin() {
  if (!isNative()) return null;
  try {
    const mod = await withTimeout(import('@ebarooni/capacitor-calendar'), 4000, null);
    return mod?.CapacitorCalendar ? { api: mod.CapacitorCalendar } : null;
  } catch {
    return null;
  }
}

export async function isDeviceCalendarAvailable() {
  return (await plugin()) !== null;
}

// Read-permission state: 'granted' | 'denied' | 'prompt' | null (unavailable).
// Time-boxed — safe to call from render effects.
export async function checkCalendarPermission() {
  const cal = (await plugin())?.api;
  if (!cal) return null;
  try {
    const perm = await withTimeout(cal.checkPermission({ scope: 'readCalendar' }), 2500, null);
    return perm?.result || null;
  } catch {
    return null;
  }
}

// The device calendars themselves (titles), for showing what's connected.
// Requires read permission; resolves null otherwise. Time-boxed.
export async function listDeviceCalendars() {
  const cal = (await plugin())?.api;
  if (!cal) return null;
  try {
    const perm = await withTimeout(cal.checkPermission({ scope: 'readCalendar' }), 2500, null);
    if (perm?.result !== 'granted') return null;
    const listed = await withTimeout(cal.listCalendars(), 4000, null);
    if (!listed?.result) return null;
    return listed.result.map((c) => ({ id: c.id, title: c.title, color: c.color || undefined }));
  } catch {
    return null;
  }
}

// Requests read access. Returns { granted, error } instead of a bare boolean so
// the UI can distinguish "user said no" from "the bridge never answered" and
// surface the real failure (device-only issues are invisible otherwise).
// error: null | 'unavailable' | 'timeout' | <native message>
export async function requestCalendarAccess() {
  const cal = (await plugin())?.api;
  if (!cal) return { granted: false, error: 'unavailable' };
  try {
    // iOS has NO read-only calendar scope — requestReadOnlyCalendarAccess is
    // `unimplemented` there and rejects immediately (nothing prompts, button
    // looks dead). EventKit reading on iOS 17+ needs full access, which the
    // NSCalendarsFullAccessUsageDescription string covers. Android keeps the
    // narrower read-only request.
    //
    // The permission sheet needs user interaction, so the window is generous —
    // but bounded, so a wedged bridge can never leave the button spinning
    // forever.
    const TIMED_OUT = Symbol('timeout');
    const res = await Promise.race([
      isIOS() ? cal.requestFullCalendarAccess() : cal.requestReadOnlyCalendarAccess(),
      new Promise((resolve) => setTimeout(() => resolve(TIMED_OUT), 60000)),
    ]);
    if (res === TIMED_OUT) return { granted: false, error: 'timeout' };
    return { granted: res?.result === 'granted', error: null };
  } catch (err) {
    console.warn('Calendar permission request failed:', err);
    return { granted: false, error: err?.message || String(err) };
  }
}

// Merge a server (Google) feed with on-device events, de-duped by title+start
// (same rule the briefing uses) and time-ordered. Shared by the Calendar page
// and the Today tile so both surfaces agree on what "today" contains.
export function mergeEvents(feedEvents, deviceEvents) {
  const seen = new Set();
  const out = [];
  for (const e of [...(feedEvents || []), ...(deviceEvents || [])]) {
    const key = `${e.title}::${e.start}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(e);
  }
  out.sort((a, b) => (a.start || '').localeCompare(b.start || ''));
  return out;
}

// Events for a YYYY-MM-DD date, shaped like the backend's CalendarEvent
// (title/start/end/allDay/meetingKind) so device and Google events merge
// cleanly. EventKit exposes attendee names but rarely emails, so meetingKind
// classification is coarser than Google's: attendees → meeting, else block.
export async function getEventsForDate(date) {
  // Timebox the whole native path (plugin import + permission + query). This
  // function is awaited by the briefing and chat too — none of them may hang.
  const cal = (await withTimeout(plugin(), 4500, null))?.api;
  if (!cal) return null;
  // Don't touch EventKit unless read access is already granted — querying
  // without authorization can block the native bridge indefinitely. The
  // permission check itself is time-boxed as a belt-and-braces guard.
  try {
    const perm = await withTimeout(
      cal.checkPermission({ scope: 'readCalendar' }),
      2500,
      { result: 'denied' },
    );
    if (perm?.result !== 'granted') return null;
  } catch {
    return null;
  }
  const from = new Date(`${date}T00:00:00`).getTime();
  const to = from + 24 * 60 * 60 * 1000;
  try {
    const listed = await withTimeout(cal.listEventsInRange({ from, to }), 4000, null);
    if (!listed) return null;
    const { result } = listed;
    return (result || []).map((e) => {
      const others = (e.attendees || []).length;
      return {
        title: e.title || '(no title)',
        start: new Date(e.startDate).toISOString(),
        end: new Date(e.endDate).toISOString(),
        allDay: !!e.isAllDay,
        location: e.location || undefined,
        attendees: others || undefined,
        meetingKind: others > 0 ? 'external_meeting' : 'time_block',
        accountLabel: 'Device calendar',
      };
    });
  } catch (err) {
    console.warn('Device calendar query failed:', err);
    return null;
  }
}
